import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@/types';

// Mock users for development/demo
const MOCK_USERS = [
  {
    email: 'admin@hospital.org',
    password: 'admin123',
    user: {
      user_id: '1',
      hospital_id: '550e8400-e29b-41d4-a716-446655440000',
      email: 'admin@hospital.org',
      full_name: 'System Administrator',
      role: 'admin' as const,
    },
  },
  {
    email: 'manager@hospital.org',
    password: 'manager123',
    user: {
      user_id: '2',
      hospital_id: '550e8400-e29b-41d4-a716-446655440000',
      email: 'manager@hospital.org',
      full_name: 'Inventory Manager',
      role: 'inventory_manager' as const,
    },
  },
  {
    email: 'staff@hospital.org',
    password: 'staff123',
    user: {
      user_id: '3',
      hospital_id: '550e8400-e29b-41d4-a716-446655440000',
      email: 'staff@hospital.org',
      full_name: 'Medical Staff',
      role: 'staff' as const,
    },
  },
];

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
  });

  const checkAuth = useCallback(async () => {
    try {
      // Check for demo mode token first (kept for backwards compatibility)
      const demoToken = localStorage.getItem('demo_token');
      const storedUser = localStorage.getItem('demo_user');

      if (demoToken === 'true' && storedUser) {
        const user = JSON.parse(storedUser);
        setState({
          isAuthenticated: true,
          user,
          isLoading: false,
          error: null,
        });
        localStorage.setItem('hospital_id', user.hospital_id.toString());
        return;
      }

      // Check for real ERP JWT token
      const accessToken = localStorage.getItem('access_token');
      const hospitalId = localStorage.getItem('hospital_id');

      if (accessToken && hospitalId) {
        // Validate token by calling /api/v1/auth/validate or /me
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/auth/me`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setState({
              isAuthenticated: true,
              user: {
                user_id: userData.user_id || userData.sub,
                hospital_id: hospitalId,
                email: userData.email,
                full_name: userData.full_name || userData.name,
                role: userData.role || 'staff',
                permissions: userData.permissions || [],
              },
              isLoading: false,
              error: null,
            });
            return;
          } else {
            // Token invalid or expired, clear it
            localStorage.removeItem('access_token');
            localStorage.removeItem('hospital_id');
          }
        } catch (error) {
          console.error('Token validation failed:', error);
        }
      }

      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Authentication check failed',
      }));
    }
  }, []);

  useEffect(() => {
    checkAuth();

    // Listen for Supabase auth changes (only if using real Supabase)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, _session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          await checkAuth();
        } else if (event === 'SIGNED_OUT') {
          setState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null,
          });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [checkAuth]);

  const login = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

      // Call inventory backend login endpoint (proxied to ERP)
      const response = await fetch(`${API_BASE}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Invalid credentials');
      }

      const { access_token, user } = await response.json();

      // Store real authentication token
      localStorage.setItem('access_token', access_token);
      // hospital_id is inside user object
      localStorage.setItem('hospital_id', user.hospital_id || user.id);
      localStorage.setItem('demo_token', 'false');

      setState({
        isAuthenticated: true,
        user: {
          user_id: user.id || user.user_id,
          hospital_id: user.hospital_id,
          email: user.email,
          full_name: user.full_name || user.name,
          role: user.role_names?.[0] || 'staff',
          permissions: user.permissions || [],
        },
        isLoading: false,
        error: null,
      });

      return { success: true };
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
      return { success: false, error: state.error };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Clear demo auth
      localStorage.removeItem('demo_token');
      localStorage.removeItem('demo_user');
      localStorage.removeItem('hospital_id');

      // Also try Supabase sign out
      await supabase.auth.signOut();
      localStorage.removeItem('access_token');

      setState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Logout failed',
      }));
    }
  }, []);

  return {
    ...state,
    login,
    logout,
    refreshAuth: checkAuth,
  };
}
